import { createDurationHistogram } from './effect-histogram.ts'
import {
  ALL_WINDOW_REASONS,
  judgeWindow,
  type MeasureBudget,
  type MeasureFrame,
  type WindowReason,
} from './effect-measure.ts'

export interface EffectWindowReport {
  at: number
  bad: boolean
  reasons: WindowReason[]
  sustained: boolean
  skipped: boolean
  durationMs: number
  deliveredFrames: number
  cameraFrames: number
  costP50Ms: number | null
  deliveredFps: number | null
  cameraFps: number | null
}

export interface EffectMonitor {
  record(frame: MeasureFrame): void
  recordCameraFrames(count: number, at: number): void
  recordRequestedFrames(count: number, at: number): void
  setSegmentationInterval(interval: number): void
  setActingReasons(reasons: readonly WindowReason[]): void
  poll(now: number): EffectWindowReport | null
  skipNextWindow(): void
  reset(): void
}

interface EffectMonitorOptions {
  budget: MeasureBudget
  segmentationInterval?: number
  sustainedWindows?: number
}

export function createEffectMonitor({
  budget,
  segmentationInterval = 1,
  sustainedWindows = 2,
}: EffectMonitorOptions): EffectMonitor {
  const cost = createDurationHistogram()
  let interval = segmentationInterval
  let acting: readonly WindowReason[] = ALL_WINDOW_REASONS
  let windowStart: number | null = null
  let delivered = 0
  let camera = 0
  let requested = 0
  let gpuBehind = 0
  let consecutiveBad = 0
  let skip = false

  function open(at: number) {
    if (windowStart === null) windowStart = at
  }

  function clearWindow() {
    delivered = 0
    camera = 0
    requested = 0
    gpuBehind = 0
    cost.reset()
  }

  return {
    record(frame) {
      open(frame.at)
      delivered += 1
      if (frame.gpuBehind) gpuBehind += 1
      if (frame.segmented) cost.add(frame.costMs)
    },
    recordCameraFrames(count, at) {
      open(at)
      if (Number.isFinite(count) && count > 0) camera += count
    },
    recordRequestedFrames(count, at) {
      open(at)
      if (Number.isFinite(count) && count > 0) requested += count
    },
    setSegmentationInterval(next) {
      interval = next
    },
    setActingReasons(reasons) {
      acting = reasons
    },
    poll(now) {
      open(now)
      if (windowStart === null || now - windowStart < budget.windowMs) return null

      const judgement = judgeWindow(
        {
          deliveredFrames: delivered,
          cameraFrames: camera,
          requestedFrames: requested,
          gpuBehindFrames: gpuBehind,
          cost,
          segmentationInterval: interval,
        },
        budget,
      )
      const bad = judgement.reasons.some((reason) => acting.includes(reason))
      const durationMs = now - windowStart
      const seconds = durationMs / 1000
      const report: EffectWindowReport = {
        at: now,
        bad,
        reasons: judgement.reasons,
        sustained: false,
        skipped: false,
        durationMs,
        deliveredFrames: delivered,
        cameraFrames: camera,
        costP50Ms: cost.percentile(50),
        deliveredFps: durationMs > 0 ? delivered / seconds : null,
        cameraFps: durationMs > 0 && camera > 0 ? camera / seconds : null,
      }
      clearWindow()
      windowStart = now

      if (skip) {
        skip = false
        consecutiveBad = 0
        report.skipped = true
        return report
      }

      consecutiveBad = bad ? consecutiveBad + 1 : 0
      report.sustained = consecutiveBad >= sustainedWindows
      return report
    },
    skipNextWindow() {
      skip = true
      consecutiveBad = 0
    },
    reset() {
      clearWindow()
      windowStart = null
      consecutiveBad = 0
      skip = false
    },
  }
}
